import React from "react";
import { tp_small } from "../../constants/icons";
import {
  POSITION_HEADER_SUFFIX,
  POSITION_HEADER_TOOLTIP,
  getPositionDisplay,
} from "../../utils/positionFormatter";

const statusMap = {
  pitch_sent: {
    label: "Pitch sent",
    className: "bg-[#F8F3ED] text-[#B97A1A]",
  },
  accepted: {
    label: "Accepted",
    className: "bg-[#EAF7F0] text-[#3BA97C]",
  },
  rejected: {
    label: "Rejected",
    className: "bg-[#FDECEF] text-[#F04B6A]",
  },
};

const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const TABLE_COLUMNS = [
  { key: "agent", label: "AGENT", className: "justify-start" },
  { key: "player", label: "PITCHED PLAYER", className: "justify-start" },
  { key: "club", label: "TO CLUB", className: "justify-start" },
  {
    key: "position",
    label: "Position",
    className: "justify-start",
    withTooltip: true,
  },
  { key: "status", label: "STATUS", className: "justify-center" },
  { key: "date", label: "DATE SENT", className: "justify-end" },
];

const PitchesTable = ({
  pitches,
  pitchesLoading,
  onAgentClick,
  onPlayerClick,
  onClubClick,
}) => {
  if (pitchesLoading) {
    return (
      <div className="w-full bg-white border border-[#D6DBEA] rounded-lg shadow-sm">
        <div className="flex items-center justify-center py-16 text-gray-400 text-lg font-medium">
          Loading pitches...
        </div>
      </div>
    );
  }

  if (pitches.length === 0) {
    return (
      <div className="w-full bg-white border border-[#D6DBEA] rounded-lg shadow-sm">
        <div className="flex items-center justify-center py-16 text-gray-400 text-lg font-medium">
          No pitches found.
        </div>
      </div>
    );
  }

  return (
    <div className="w-full bg-white border border-[#D6DBEA] rounded-lg shadow-sm overflow-x-auto">
      <div className="min-w-[1100px]">
        {/* Table Header */}
        <div className="grid grid-cols-6 gap-6 py-3 px-6 bg-[#F7F7F7] rounded-t-lg sticky top-0 z-10">
          {TABLE_COLUMNS.map((col) => (
            <div
              key={col.key}
              className={`text-[#222A35] text-xs sm:text-[14px] font-medium uppercase tracking-wide whitespace-nowrap ${col.className}`}
            >
              {col.withTooltip ? (
                <span
                  className="inline-flex items-center gap-1 normal-case"
                  title={POSITION_HEADER_TOOLTIP}
                >
                  <span>{col.label}</span>
                  <span className="text-[10px] sm:text-xs text-[#667085] font-normal">
                    {POSITION_HEADER_SUFFIX}
                  </span>
                </span>
              ) : (
                col.label
              )}
            </div>
          ))}
        </div>

        {/* Table Body */}
        {pitches.map((pitch) => (
          <div
            key={pitch.id}
            className="grid grid-cols-6 gap-6 items-center py-4 px-6 border-b last:border-b-0 bg-white"
          >
            {/* Agent Column */}
            <div
              onClick={() => onAgentClick(pitch.agent?.id)}
              className="cursor-pointer flex items-center gap-3 whitespace-nowrap"
            >
              {pitch.agent.image ? (
                <img
                  src={pitch.agent.image}
                  alt={pitch.agent.name}
                  className="w-9 h-9 rounded-full object-cover"
                  onError={(e) => {
                    e.target.style.display = "none";
                    e.target.nextSibling.style.display = "flex";
                  }}
                />
              ) : (
                <div
                  className="w-9 h-9 rounded-full bg-gray-200 flex items-center justify-center text-sm font-medium text-gray-600"
                  style={{ display: "flex" }}
                >
                  {pitch.agent.name?.charAt(0) || "A"}
                </div>
              )}

              <span className="font-bold text-[#222A35] text-xs sm:text-[16px]">
                {pitch.agent.name}
              </span>
            </div>

            {/* Player Column */}
            <div className="flex items-center gap-3 whitespace-nowrap">
              <div
                onClick={() => onPlayerClick(pitch.player?.id)}
                className="cursor-pointer w-9 h-9 rounded-full object-cover relative"
              >
                {pitch.player?.image ? (
                  <img
                    src={pitch.player.image}
                    alt={pitch.player.name}
                    className="w-full h-full rounded-full object-cover"
                    onError={(e) => {
                      e.target.style.display = "none";
                      e.target.nextSibling.style.display = "flex";
                    }}
                  />
                ) : (
                  <div
                    className="w-full h-full rounded-full bg-gray-200 flex items-center justify-center text-sm font-medium text-gray-600"
                    style={{ display: "flex" }}
                  >
                    {pitch.player.name?.charAt(0) || "P"}
                  </div>
                )}

                {pitch.player.isVerified && (
                  <span className="absolute bottom-0 right-0 ml-1 inline-block align-middle">
                    <img src={tp_small} alt="tp_small" className="w-4 h-4" />
                  </span>
                )}
              </div>

              <span
                title={pitch.player.name}
                className="font-medium text-[#222A35] text-xs sm:text-[16px] block truncate max-w-[180px]"
              >
                {pitch.player.name}
              </span>
            </div>

            {/* Club Column */}
            <div
              onClick={() => onClubClick(pitch.club.id)}
              className="flex cursor-pointer items-center gap-2 whitespace-nowrap"
            >
              {pitch?.club?.logo ? (
                <img
                  src={pitch.club.logo}
                  alt={pitch.club.name}
                  className="w-8 h-8 rounded-full object-cover"
                  onError={(e) => {
                    e.target.style.display = "none";
                    e.target.nextSibling.style.display = "flex";
                  }}
                />
              ) : (
                <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-sm font-medium text-gray-600">
                  {pitch.club.name?.charAt(0) || "C"}
                </div>
              )}

              <span className="text-[#222A35] text-xs sm:text-[16px]">
                {pitch.club.name}
              </span>
            </div>

            {/* Position Column */}
            <div className="text-[#222A35] text-xs sm:text-[16px] whitespace-nowrap">
              {pitch.positionDisplay ||
                getPositionDisplay(pitch.player, pitch.position)}
            </div>

            {/* Status Column */}
            <div className="flex whitespace-nowrap">
              <span
                className={`inline-block rounded-lg px-4 py-1 text-xs sm:text-[16px] font-medium ${
                  statusMap[pitch.status]?.className ||
                  "bg-gray-100 text-gray-500"
                }`}
              >
                {statusMap[pitch.status]?.label || pitch.status}
              </span>
            </div>

            {/* Date Column */}
            <div className="flex text-[#222A35] text-xs sm:text-[16px] whitespace-nowrap">
              {formatDate(pitch.dateSent)}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default PitchesTable;
